import { useEffect, useState } from 'react';
import type { Checklist, UpdateChecklistInput } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useDialog } from '../../context/DialogContext';

export interface TaskListSettingsModalProps {
  checklist: Checklist | null;
  onClose: () => void;
  onSave: (updates: UpdateChecklistInput) => void;
  saving?: boolean;
}

interface Choice {
  key: 'dueTimeEnabled' | 'includeInDigest';
  label: string;
  hint: string;
}

const CHOICES: Choice[] = [
  {
    key: 'dueTimeEnabled',
    label: 'Due dates include a time',
    hint: 'Each task is due at a time of day as well as on a date. Off, a task is due on its date only, and any times already set are cleared.',
  },
  {
    key: 'includeInDigest',
    label: 'Include in the daily task email',
    hint: 'This list’s tasks appear in the morning email of the people they are assigned to. Off, they are left out of it.',
  },
];

/** How one task list behaves: whether its due dates carry a time, and whether it is in the daily email. */
export function TaskListSettingsModal({
  checklist,
  onClose,
  onSave,
  saving = false,
}: TaskListSettingsModalProps) {
  const { confirm } = useDialog();
  const [draft, setDraft] = useState({ dueTimeEnabled: false, includeInDigest: true });

  useEffect(() => {
    if (checklist) {
      setDraft({
        dueTimeEnabled: !!checklist.dueTimeEnabled,
        includeInDigest: checklist.includeInDigest !== false,
      });
    }
  }, [checklist]);

  if (!checklist) return null;
  const title =
    (checklist.isTemplate ? checklist.templateName : null) || checklist.title || 'Untitled list';
  const changed: UpdateChecklistInput = {};
  if (draft.dueTimeEnabled !== !!checklist.dueTimeEnabled) {
    changed.dueTimeEnabled = draft.dueTimeEnabled;
  }
  if (draft.includeInDigest !== (checklist.includeInDigest !== false)) {
    changed.includeInDigest = draft.includeInDigest;
  }
  const dirty = Object.keys(changed).length > 0;
  const timedTasks = checklist.sections
    .flatMap((section) => section.items)
    .filter((task) => task.dueTime).length;

  async function save() {
    if (
      changed.dueTimeEnabled === false &&
      timedTasks > 0 &&
      !(await confirm({
        title: 'Clear due times',
        message: `${timedTasks} task${timedTasks === 1 ? ' has a' : 's have'} a due time. Turning times off clears ${timedTasks === 1 ? 'it' : 'them'}; the dates stay.`,
        confirmLabel: 'Clear times',
        variant: 'warning',
      }))
    ) {
      return;
    }
    onSave(changed);
  }

  return (
    <EntityModal
      open
      onClose={onClose}
      title="List settings"
      size="sm"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 -mt-1 mb-4">{title}</p>
      <div className="space-y-3">
        {CHOICES.map((choice) => (
          <label
            key={choice.key}
            className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={draft[choice.key]}
              onChange={(e) => setDraft((d) => ({ ...d, [choice.key]: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{choice.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{choice.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </EntityModal>
  );
}
